import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Star, ArrowRight, Clock, Calendar, MessageCircle, ShoppingBag, Info } from "lucide-react";
import { api, formatBRL } from "../../lib/api";
import { hourBankName, economia, canonicalCategory, packagePricing } from "../../lib/hourbank";
import { useAuth } from "../../context/AuthContext";

const WHATSAPP_NUMBER = "5521975968405";

function priceLabel(p) {
  // Devolve {value, suffix, prefix} para renderização compacta
  if (p.pricing_mode === "package") {
    if (!p.price_package) return { prefix: "", value: "Sob consulta", suffix: "" };
    return { prefix: "", value: formatBRL(p.price_package), suffix: ` · ${p.package_hours}h` };
  }
  if (p.pricing_mode === "daily" || p.price_daily) {
    return { prefix: p.pricing_mode === "hourly" ? "a partir de " : "", value: formatBRL(p.price_daily || 0), suffix: "/dia" };
  }
  if (p.pricing_mode === "hourly" || p.price_hourly) {
    return { prefix: "a partir de ", value: formatBRL(p.price_hourly || 0), suffix: "/hora" };
  }
  if (p.pricing_mode === "monthly" || p.price_monthly) {
    return { prefix: "", value: formatBRL(p.price_monthly || 0), suffix: "/mês" };
  }
  if (p.pricing_mode === "custom") {
    return { prefix: "", value: "Sob consulta", suffix: "" };
  }
  return { prefix: "", value: formatBRL(p.price_monthly || 0), suffix: "/mês" };
}

export default function Planos() {
  const [plans, setPlans] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("Todos");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/public/plans").then(({ data }) => setPlans(data)),
      api.get("/public/categories").then(({ data }) => setCategories(data)),
      api.get("/public/spaces").then(({ data }) => setSpaces(data)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleBuy = (planId, e) => {
    e.preventDefault();
    // Novo fluxo público — sem exigir login prévio.
    // Reutiliza pipeline de reservas (PIX MP + webhook + ativação automática).
    nav(`/comprar-plano/${planId}`);
  };

  // Para planos avulsos / mensais (Day Use, Sala de Reunião, Escritório Virtual etc.):
  // direciona para o portal de reservas com a categoria pré-filtrada.
  const handleReserve = (plan, e) => {
    e.preventDefault();
    const cat = encodeURIComponent(plan.category || "");
    const target = `/portal/reservas?categoria=${cat}`;
    if (user && user !== false) {
      if (user.role === "client") nav(target);
      else nav("/admin");
    } else {
      const back = encodeURIComponent(target);
      nav(`/login?next=${back}`);
    }
  };

  // Decide o CTA principal de cada plano (sem regredir Pacotes).
  const planCta = (p) => {
    // 1) Pacotes de horas → "Comprar agora" (já era assim)
    if (p.pricing_mode === "package" && p.price_package > 0) {
      return { label: "Comprar agora", onClick: (e) => handleBuy(p.id, e), testid: `plan-buy-${p.id}` };
    }
    // 2) Escritório Virtual (mensalidade) → WhatsApp para fechar (não há checkout público de subscription)
    if (p.type === "escritorio_virtual" && (p.price_monthly || 0) > 0) {
      return { label: "Contratar via WhatsApp", external: true, testid: `plan-subscribe-${p.id}` };
    }
    // 3) Day Use / Sala Reunião / Avulsos → "Reservar agora"
    if (p.pricing_mode === "daily" || p.pricing_mode === "hourly" ||
        ["day_use", "sala_reuniao", "escritorio_executivo", "escritorio_executivo_premium",
         "consultorio", "avulso"].includes(p.type)) {
      return { label: "Reservar agora", onClick: (e) => handleReserve(p, e), testid: `plan-reserve-${p.id}` };
    }
    // 4) Fallback (custom / sob consulta) → WhatsApp
    return { label: "Fale conosco", external: true, testid: `plan-contact-${p.id}` };
  };

  const grouped = useMemo(() => {
    const byCat = {};
    plans.forEach((p) => {
      const c = canonicalCategory(p);
      if (!byCat[c]) byCat[c] = [];
      byCat[c].push(p);
    });
    // RC6 — ordem: Experience → Start → Professional → Business → Corporate VIP
    Object.values(byCat).forEach((list) => list.sort((a, b) => {
      const w = (p) => p.pricing_mode === "package" ? (p.package_hours || 999) : 10000 + (p.display_order || 0);
      return w(a) - w(b);
    }));
    return byCat;
  }, [plans]);

  const visibleCats = activeCat === "Todos"
    ? categories.map(c => c.name).filter(n => grouped[n]?.length)
    : [activeCat];

  return (
    <div data-testid="planos-page" className="pb-24">
      <section className="pt-24 pb-12 bg-[#0A1128]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Banco de Horas</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-tight">
            Compre horas e utilize
            <span className="block italic text-[#D4AF37] mt-2">quando precisar.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/60 mt-6 max-w-2xl mx-auto">
            Você escolhe a quantidade de horas e utiliza conforme sua necessidade.
            Bancos de 4h a 96h com validade estendida. Sem fidelidade, sem surpresas.
          </p>

          {/* Filtros por categoria */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setActiveCat("Todos")}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition ${activeCat === "Todos" ? "bg-[#D4AF37] text-[#0A1128]" : "border border-white/10 text-white/60 hover:text-white"}`}
              data-testid="cat-filter-todos"
            >Todos</button>
            {categories.map(c => (
              <button
                key={c.slug}
                onClick={() => setActiveCat(c.name)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition ${activeCat === c.name ? "bg-[#D4AF37] text-[#0A1128]" : "border border-white/10 text-white/60 hover:text-white"}`}
                data-testid={`cat-filter-${c.slug}`}
              >{c.name}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Banner explicativo: categoria x pacote */}
          <div className="mb-10 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 flex items-start gap-3" data-testid="category-package-banner">
            <Info size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-white/80 leading-relaxed">
              <strong className="text-[#D4AF37]">Como funcionam os pacotes:</strong> cada espaço pertence a uma categoria
              (Coworking, Escritório Executivo, Consultório, Sala de Reunião). Ao adquirir um pacote da categoria
              correspondente, suas horas são <em>debitadas automaticamente</em> sem cobrança adicional na reserva.
            </div>
          </div>
          {loading ? (
            <div className="text-center text-white/50 py-20">Carregando planos…</div>
          ) : visibleCats.length === 0 ? (
            <div className="text-center text-white/50 py-20">Nenhum banco de horas nessa categoria.</div>
          ) : (
            visibleCats.map((cat) => (
              <div key={cat} className="mb-14" data-testid={`cat-section-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                <div className="gold-divider mb-6" />
                <h2 className="font-display text-2xl sm:text-3xl text-white mb-2">{cat}</h2>
                <p className="text-sm text-white/50 mb-8">{categories.find(c => c.name === cat)?.description}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plans-grid">
                  {grouped[cat].map((p) => {
                    const price = priceLabel(p);
                    const isPackage = p.pricing_mode === "package" && p.package_hours;
                    // Precificação dinâmica: taxa avulsa oficial × desconto progressivo
                    const pricing = isPackage ? packagePricing(p, spaces) : null;
                    const waMsg = `Olá! Tenho interesse no "${isPackage ? hourBankName(p) : p.name}" do VM Corporate Piabetá.`;
                    const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMsg)}`;
                    return (
                      <div
                        key={p.id}
                        className={`card-marketing p-7 flex flex-col ${p.highlight || p.package_hours === 32 ? "border-[#D4AF37]/60 bg-[#D4AF37]/5 tracing-line" : ""}`}
                        data-testid={`plan-card-${p.id}`}
                      >
                        {p.package_hours === 32 ? (
                          <div className="inline-flex items-center gap-1 self-start mb-4 px-3 py-1 bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-[0.18em] font-bold rounded-full">
                            <Star size={11} /> Mais vendido
                          </div>
                        ) : p.highlight ? (
                          <div className="inline-flex items-center gap-1 self-start mb-4 px-3 py-1 bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-[0.18em] font-bold rounded-full">
                            <Star size={11} /> Mais escolhido
                          </div>
                        ) : null}
                        <div className="font-display text-xl text-white mb-2">{isPackage ? hourBankName(p) : p.name}</div>
                        <div className="text-sm text-white/55 leading-relaxed mb-5">{p.description}</div>

                        <div className="mb-5 pb-5 border-b border-white/5">
                          {isPackage ? (
                            <>
                              <div className="flex items-baseline gap-1 flex-wrap">
                                <span className="font-display text-3xl sm:text-4xl text-[#D4AF37]">{formatBRL(pricing?.perHour ?? (p.price_package / p.package_hours))}</span>
                                <span className="text-sm text-white/50">/ hora</span>
                              </div>
                              <div className="mt-2 flex items-center gap-3 text-[11px] text-white/50">
                                <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.package_hours} horas</span>
                                <span className="inline-flex items-center gap-1"><Calendar size={11} /> {p.validity_days || 30} dias</span>
                              </div>
                              <div className="mt-1.5 text-xs text-white/45">Total do pacote: {formatBRL(pricing?.total ?? p.price_package)}</div>
                            </>
                          ) : (
                            <>
                              {price.prefix && (
                                <span className="text-[10px] uppercase tracking-wider text-white/40">{price.prefix}</span>
                              )}
                              <div className="flex items-baseline gap-1 flex-wrap">
                                <span className="font-display text-3xl sm:text-4xl text-[#D4AF37]">{price.value}</span>
                                <span className="text-sm text-white/50">{price.suffix}</span>
                              </div>
                            </>
                          )}
                          {isPackage && (() => {
                            const eco = pricing && pricing.save > 0.5
                              ? { avulso: pricing.avulso, save: pricing.save }
                              : economia(p, spaces);
                            if (!eco) return null;
                            return (
                              <div className="mt-3 space-y-0.5 text-xs" data-testid={`plan-eco-${p.id}`}>
                                <div className="text-white/40">Valor avulso: <s>{formatBRL(eco.avulso)}</s></div>
                                <div className="text-emerald-400 font-bold text-sm">Você economiza {formatBRL(eco.save)}</div>
                              </div>
                            );
                          })()}
                        </div>

                        <ul className="space-y-2.5 mb-6 flex-1">
                          {(p.features || []).map((f, i) => (
                            <li key={i} className="flex gap-2.5 text-sm text-white/75 leading-relaxed">
                              <CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="flex flex-col sm:flex-row gap-2">
                          {(() => {
                            const cta = planCta(p);
                            if (cta.external) {
                              return (
                                <a href={waHref} target="_blank" rel="noreferrer"
                                  className="btn-gold inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm flex-1"
                                  data-testid={cta.testid}
                                >
                                  <MessageCircle size={14} /> {cta.label}
                                </a>
                              );
                            }
                            return (
                              <button type="button" onClick={cta.onClick}
                                className="btn-gold inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm flex-1"
                                data-testid={cta.testid}
                              >
                                <ShoppingBag size={14} /> {cta.label}
                              </button>
                            );
                          })()}
                          <a
                            href={waHref}
                            target="_blank" rel="noreferrer"
                            className="btn-ghost-gold inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm flex-1"
                            data-testid={`plan-whatsapp-${p.id}`}
                          >
                            <MessageCircle size={14} /> WhatsApp
                          </a>
                          <Link
                            to="/contato"
                            className="btn-ghost-gold inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm flex-1"
                            data-testid={`plan-cta-${p.id}`}
                          >
                            Tour <ArrowRight size={14} />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
